import { Injectable } from '@angular/core';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/**
 * This service is used to trigger certain actions before taking actions.
 *
 * eg. When you log out while you were editing a form, you need to show confirmation modal.
 * If the user don't want to log out, you have to cancel the logout action.
 *
 * TODO: This service needs to be reviewed and refactored.
 * I don't see a reason not to have multiple subjects for a hook name.
 */
@Injectable({
  providedIn: 'root',
})
export class HooksService {
  // Map of hook name to registered hooks subject
  private _hooks = new Map<string, Subject<boolean>[]>();

  constructor() {}

  /**
   * Register a hook
   *
   * @param hookName Name of the hook
   * @returns Subject to trigger actions
   */
  addHook(hookName: string, single?: boolean): Subject<boolean> {
    const hook = new Subject<boolean>();

    if (!this._hooks.has(hookName)) {
      this._hooks.set(hookName, []);
    }
    const hooks = this._hooks.get(hookName);

    if (single && hooks.length > 0) {
      console.debug('Hook exists. Returning ', hookName, hooks.length);
      return hooks[0];
    }

    hooks.push(hook);
    console.debug('Added hook', hookName, hooks.length);

    return hook;
  }

  /**
   * Remove registered hook.
   */
  removeHook(hookName: string, hook: Subject<boolean>) {
    if (!this._hooks.has(hookName)) {
      return;
    }

    const hooks = this._hooks.get(hookName);
    const index = hooks.indexOf(hook);
    hooks.splice(index, 1);
  }

  /**
   * Trigger hooks
   *
   * @param hookName hook name
   * @param value
   */
  callHook(hookName: string, value = false) {
    const hooks = this._hooks.get(hookName) || [];
    hooks.forEach((hook) => {
      hook.next(value);
    });
  }

  /**
   * Subscribe to a hook
   *
   * @param hookName hook name
   * @param callback callback to be triggered
   * @returns
   */
  subscribeToHook(hookName: string, callback: () => void): Subscription {
    const hooks = this._hooks.get(hookName) || [];
    if (hooks.length === 0) {
      callback();
      return;
    }

    const subscription = combineLatest(hooks)
      .pipe(
        map((values: boolean[]) => values.every((v) => v)),
        filter((value) => value)
      )
      .subscribe(callback);

    this.callHook(hookName);

    return subscription;
  }
}
